import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { AutoCompleteAddress } from 'shared/types/account/Account';
import Icon from '@/components/customercenter-ui/svgs/info';
import useIntuitiveSearch from '@/frontastic/hooks/useIntuitiveSearch';
import { extractAddressComponents } from '@/helpers/utils/siteUtils';
import FormFields from './validateFormFields';
import { FORM_FIELD_SPECS } from '../../../../../constants/constant';
import { AddressSchema } from '../../customer-detail/schema';
import { DifferenceHighlighter } from '../../order-details/util/differenceHighlighter';
import { Button } from '../../ui/button';
import { RadioGroup, RadioGroupItem } from '../../ui/customRadio/customRadio';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Label } from '../../ui/label';

function setSelectAddressValue(validateAddressResponse: any) {
  if (validateAddressResponse?.result === 'premises partial' || validateAddressResponse?.result === 'no matches') {
    return 'edit';
  } else if (validateAddressResponse?.result == 'multiple matches') {
    return 'recommended';
  } else {
    return 'recommended';
  }
}
export function AddressDialogModal({
  openModal,
  setOpenModal,
  values,
  validateAddressResponse,
  mergeDataWithFormData,
  setFormattingKey,
  Texts,
}: any) {
  const [selectAddress, setSelectAddress] = useState(setSelectAddressValue(validateAddressResponse));
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [pickListSelectedAddress, setPickListSelectedAddress] = useState<AutoCompleteAddress>();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [initialStateValue, setInitialStateValue] = useState('');
  const [showadditionalAddressInfo, setShowadditionalAddressInfo] = useState(
    values?.additionalAddressInfo ? true : false,
  );
  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors },
    clearErrors,
  } = useForm({
    resolver: zodResolver(AddressSchema),
    defaultValues: values && {
      streetName: values?.streetName,
      additionalAddressInfo: values?.additionalAddressInfo,
      state: values?.state,
      city: values?.city,
      postalCode: values?.postalCode,
      firstName: values?.firstName,
      lastName: values?.lastName,
      phone: values?.phone,
    },
    mode: 'all',
  });

  const handleShowAdditionalAddressInfo = () => {
    setShowadditionalAddressInfo(true);
  };

  const handleRadioChange = (value: string) => {
    setSelectAddress(value);
  };

  const onSubmit = async (data: any) => {
    setOpenModal(false);
    if (selectAddress === 'edit') {
      data.isEditSelectedFromModel = true;
    }
    mergeDataWithFormData(data);
  };

  useIntuitiveSearch({
    setShowadditionalAddressInfo,
    setValue,
    setInitialStateValue,
    clearErrors,
    setPickListSelectedAddress,
  });

  const handleSelectSuggestedAddress = (data: any) => {
    setFormattingKey(data?.suggestionAddressKey);
    const suggestedAddress: any = extractAddressComponents(data?.suggestionAddress, data?.suggestionZip);
    Object.keys(suggestedAddress).forEach((fieldName: any) => {
      setValue(fieldName, suggestedAddress[fieldName]);
    });
  };

  return (
    <Dialog open={openModal} onOpenChange={setOpenModal}>
      <DialogContent className="max-w-[667px] bg-white sm:rounded-md">
        <DialogHeader>
          <DialogTitle className="text-left text-xl font-bold">{Texts.verifyAddress}</DialogTitle>

          {validateAddressResponse?.result === 'premises partial' ? (
            <DialogDescription className="pb-4 pt-2 text-left">{Texts.partialPremisesDescription}</DialogDescription>
          ) : validateAddressResponse?.result === 'street partial' ? (
            <DialogDescription className="pb-4 pt-2 text-left">{Texts.streetPartialDescription}</DialogDescription>
          ) : validateAddressResponse?.result === 'no matches' ? (
            <DialogDescription className="pb-4 pt-2 text-left">
              {Texts.noMatchDescription}
              {Texts.noMatchDescription1}
            </DialogDescription>
          ) : validateAddressResponse?.result === 'multiple matches' ||
            validateAddressResponse?.result === 'interaction required' ? (
            <DialogDescription className="pb-4 pt-2 text-left">{Texts.verifyAddressDescription}</DialogDescription>
          ) : (
            <></>
          )}
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
          {validateAddressResponse?.result === 'premises partial' ? (
            <section>
              {validateAddressResponse?.SuggestedAddresses?.length > 1 ? (
                <RadioGroup defaultValue={selectAddress} className="gap-5">
                  <section className="flex items-center space-x-2">
                    <RadioGroupItem value="edit" id="r1" onClick={() => handleRadioChange('edit')} />
                    <Label htmlFor="r1">
                      <section className="flex flex-col lg:flex-row lg:items-baseline">
                        <div className="lg:flex lg:flex-col">
                          <p className="text-base font-semibold">{Texts.editYourAddress}</p>
                          <p className="text-xs font-normal">*{Texts.requiredFields}</p>
                        </div>
                        <p className="font-xs font-normal text-grayDark lg:pl-1">{Texts.recommended}</p>
                      </section>
                    </Label>
                  </section>
                  {selectAddress === 'edit' ? (
                    <>
                      <section className="mt-2 flex max-w-[356px] items-center gap-2 border-l-4 border-l-semanticGold bg-almond py-3 text-sm text-semanticGold lg:ml-6">
                        <Icon className="ml-3 h-5 w-5" />
                        {Texts.verifyEditAddressAlert}
                      </section>
                      <FormFields
                        Texts={Texts}
                        FORM_FIELD_SPECS={FORM_FIELD_SPECS}
                        register={register}
                        errors={errors}
                        showadditionalAddressInfo={showadditionalAddressInfo}
                        handleShowAdditionalAddressInfo={handleShowAdditionalAddressInfo}
                        values={values}
                        setValue={setValue}
                      />
                    </>
                  ) : (
                    <></>
                  )}

                  <section className="flex items-center space-x-2">
                    <RadioGroupItem value="recommended" id="r2" onClick={() => handleRadioChange('recommended')} />
                    <Label htmlFor="r2">
                      <p className="text-base font-semibold">{Texts.suggestedAddress}</p>
                    </Label>
                  </section>
                  {selectAddress === 'recommended' ? (
                    <section className="ml-6">
                      <RadioGroup className="gap-5">
                        {validateAddressResponse?.SuggestedAddresses?.map((element: any) => (
                          <>
                            <section className="flex items-center space-x-2">
                              <RadioGroupItem
                                value={element.suggestionAddressKey}
                                id={element.suggestionAddressKey}
                                onClick={() => {
                                  handleSelectSuggestedAddress(element);
                                }}
                              />
                              <section className="pb-3">
                                <DifferenceHighlighter
                                  string1={element?.suggestionAddress}
                                  string2={
                                    values.streetName +
                                    ' ' +
                                    values.additionalAddressInfo +
                                    ' ' +
                                    values.city +
                                    ' ' +
                                    values.state
                                  }
                                />
                                <DifferenceHighlighter string1={element?.suggestionZip} string2={values.postalCode} />
                              </section>
                            </section>
                          </>
                        ))}
                      </RadioGroup>
                    </section>
                  ) : (
                    <></>
                  )}

                  <section className="flex items-center space-x-2">
                    <RadioGroupItem value="original" id="r3" onClick={() => handleRadioChange('original')} />
                    <Label htmlFor="r3" className="text-base font-semibold">
                      {Texts.originalAddress}
                    </Label>
                  </section>
                  {selectAddress === 'original' ? (
                    <section className="ml-6">
                      <section className="mt-2 flex max-w-[356px] items-center gap-2 border-l-4 border-l-semanticGold bg-almond py-3 text-sm text-black">
                        <Icon className="ml-3 h-5 w-5" />
                        {Texts.originalAddressAlert}
                      </section>
                      <section className="pt-5">
                        <p>{values?.streetName}</p>
                        <p> {values?.additionalAddressInfo}</p>
                        <p>{values?.city}</p>
                        <p>
                          {' '}
                          {values?.state} {values?.postalCode}
                        </p>
                      </section>
                    </section>
                  ) : (
                    <></>
                  )}
                </RadioGroup>
              ) : (
                <RadioGroup defaultValue={selectAddress} className="gap-5">
                  <section className="flex items-center space-x-2">
                    <RadioGroupItem value="edit" id="r1" onClick={() => handleRadioChange('edit')} />
                    <Label htmlFor="r1">
                      <p className="text-base font-semibold">{Texts.editYourAddress}</p>
                      {selectAddress === 'edit' ? (
                        <>
                          <p className="text-xs font-normal">{Texts.requiredFields}</p>
                          <p className="font-xs font-normal text-grayDark lg:pl-1">{Texts.recommended}</p>
                        </>
                      ) : (
                        <></>
                      )}
                    </Label>
                  </section>
                  {selectAddress === 'edit' ? (
                    <>
                      <section className="mt-2 flex max-w-[356px] items-center gap-2 border-l-4 border-l-semanticGold bg-almond py-3 text-sm text-semanticGold lg:ml-6">
                        <Icon className="ml-3 h-5 w-5" />
                        {Texts.verifyEditAddressAlert}
                      </section>
                      <FormFields
                        Texts={Texts}
                        FORM_FIELD_SPECS={FORM_FIELD_SPECS}
                        register={register}
                        errors={errors}
                        showadditionalAddressInfo={showadditionalAddressInfo}
                        handleShowAdditionalAddressInfo={handleShowAdditionalAddressInfo}
                        values={values}
                        setValue={setValue}
                      />
                    </>
                  ) : (
                    <></>
                  )}

                  <section className="flex items-center space-x-2">
                    <RadioGroupItem value="original" id="r2" onClick={() => handleRadioChange('original')} />
                    <Label htmlFor="r2" className="text-base font-semibold">
                      {Texts.originalAddress}
                    </Label>
                  </section>
                  {selectAddress === 'original' ? (
                    <section className="ml-6">
                      <section className="mt-2 flex max-w-[356px] items-center gap-2 border-l-4 border-l-semanticGold bg-almond py-3 text-sm text-black">
                        <Icon className="ml-3 h-5 w-5" />
                        {Texts.originalAddressAlert}
                      </section>
                      <section className="pt-5">
                        <p>{values?.streetName}</p>
                        <p> {values?.additionalAddressInfo}</p>
                        <p>{values?.city}</p>
                        <p>
                          {' '}
                          {values?.state} {values?.postalCode}
                        </p>
                      </section>
                    </section>
                  ) : (
                    <></>
                  )}
                </RadioGroup>
              )}
            </section>
          ) : validateAddressResponse?.result === 'street partial' ? (
            <>
              <section>
                <RadioGroup defaultValue={selectAddress} className="gap-5">
                  <section className="flex items-center space-x-2">
                    <RadioGroupItem value="edit" id="r1" onClick={() => handleRadioChange('edit')} />
                    <Label htmlFor="r1">
                      <section className="flex flex-col lg:flex-row lg:items-baseline">
                        <div className="lg:flex lg:flex-col">
                          <p className="text-base font-semibold">{Texts.editYourAddress}</p>
                          <p className="text-xs font-normal">{Texts.requiredFields}</p>
                        </div>
                        <p className="font-xs font-normal text-grayDark lg:pl-1">{Texts.recommended}</p>
                      </section>
                    </Label>
                  </section>
                  {selectAddress === 'edit' ? (
                    <>
                      <section className="mt-2 flex max-w-[356px] items-center gap-2 border-l-4 border-l-semanticGold bg-almond py-3 text-sm text-semanticGold lg:ml-6">
                        <Icon className="ml-3 h-5 w-5" />
                        {Texts.verifyEditAddressAlert}
                      </section>
                      <FormFields
                        Texts={Texts}
                        FORM_FIELD_SPECS={FORM_FIELD_SPECS}
                        register={register}
                        errors={errors}
                        showadditionalAddressInfo={showadditionalAddressInfo}
                        handleShowAdditionalAddressInfo={handleShowAdditionalAddressInfo}
                        values={values}
                        setValue={setValue}
                      />
                    </>
                  ) : (
                    <></>
                  )}

                  <section className="flex items-center space-x-2">
                    <RadioGroupItem value="original" id="r2" onClick={() => handleRadioChange('original')} />
                    <Label htmlFor="r2" className="text-base font-semibold">
                      {Texts.originalAddress}
                    </Label>
                  </section>
                  {selectAddress === 'original' ? (
                    <section className="ml-6">
                      <section className="mt-2 flex max-w-[356px] items-center gap-2 border-l-4 border-l-semanticGold bg-almond py-3 text-sm text-black">
                        <Icon className="ml-3 h-5 w-5" />
                        {Texts.originalAddressAlert}
                      </section>
                      <section className="pt-5">
                        <p>{values?.streetName}</p>
                        <p> {values?.additionalAddressInfo}</p>
                        <p>{values?.city}</p>
                        <p>
                          {' '}
                          {values?.state} {values?.postalCode}
                        </p>
                      </section>
                    </section>
                  ) : (
                    <></>
                  )}
                </RadioGroup>
              </section>
            </>
          ) : validateAddressResponse?.result === 'no matches' ? (
            <section>
              <RadioGroup defaultValue={selectAddress} className="gap-5">
                <section className="flex items-center space-x-2">
                  <RadioGroupItem value="edit" id="r1" onClick={() => handleRadioChange('edit')} />
                  <Label htmlFor="r1">
                    <section className="flex flex-col lg:flex-row lg:items-baseline">
                      <div className="lg:flex lg:flex-col">
                        <p className="text-base font-semibold">{Texts.editYourAddress}</p>
                        <p className="text-xs font-normal">{Texts.requiredFields}</p>
                      </div>
                      <p className="font-xs font-normal text-grayDark lg:pl-1">{Texts.recommended}</p>
                    </section>
                  </Label>
                </section>
                {selectAddress === 'edit' ? (
                  <>
                    <section className="mt-2 flex max-w-[356px] items-center gap-2 border-l-4 border-l-semanticGold bg-almond py-3 text-sm text-semanticGold lg:ml-6">
                      <Icon className="ml-3 h-5 w-5" />
                      {Texts.verifyEditAddressAlert}
                    </section>
                    <FormFields
                      Texts={Texts}
                      FORM_FIELD_SPECS={FORM_FIELD_SPECS}
                      register={register}
                      errors={errors}
                      showadditionalAddressInfo={showadditionalAddressInfo}
                      handleShowAdditionalAddressInfo={handleShowAdditionalAddressInfo}
                      values={values}
                      setValue={setValue}
                    />
                  </>
                ) : (
                  <></>
                )}

                <section className="flex items-center space-x-2">
                  <RadioGroupItem value="original" id="r2" onClick={() => handleRadioChange('original')} />
                  <Label htmlFor="r2" className="text-base font-semibold">
                    {Texts.originalAddress}
                  </Label>
                </section>
                {selectAddress === 'original' ? (
                  <section className="ml-6">
                    <section className="mt-2 flex max-w-[356px] items-center gap-2 border-l-4 border-l-semanticGold bg-almond py-3 text-sm text-black">
                      <Icon className="ml-3 h-5 w-5" />
                      {Texts.originalAddressAlert}
                    </section>
                    <section className="pt-5">
                      <p>{values?.streetName}</p>
                      <p> {values?.additionalAddressInfo}</p>
                      <p>{values?.city}</p>
                      <p>
                        {' '}
                        {values?.state} {values?.postalCode}
                      </p>
                    </section>
                  </section>
                ) : (
                  <></>
                )}
              </RadioGroup>
            </section>
          ) : validateAddressResponse?.result === 'multiple matches' ||
            validateAddressResponse?.result === 'interaction required' ? (
            <section>
              <RadioGroup defaultValue={selectAddress} className="gap-5">
                <section className="flex items-center space-x-2">
                  <RadioGroupItem value="recommended" id="r1" onClick={() => handleRadioChange('recommended')} />
                  <Label htmlFor="r1">
                    <section className="flex flex-col lg:flex-row lg:items-center">
                      <p className="text-base font-semibold">{Texts.suggestedAddress}</p>
                      <p className="font-xs font-normal text-grayDark lg:pl-1">{Texts.recommended}</p>
                    </section>
                  </Label>
                </section>
                {selectAddress === 'recommended' ? (
                  <section className="ml-6">
                    <RadioGroup className="gap-5">
                      {validateAddressResponse?.SuggestedAddresses?.map((element: any) => (
                        <section className="flex items-center space-x-2" key={element?.suggestionAddressKey}>
                          <RadioGroupItem
                            value={element.suggestionAddressKey}
                            id={element.suggestionAddressKey}
                            onClick={() => setFormattingKey(element.suggestionAddressKey)}
                          />
                          <section className="pb-3">
                            <DifferenceHighlighter
                              string1={element?.suggestionAddress}
                              string2={
                                values.streetName +
                                ' ' +
                                values.additionalAddressInfo +
                                ' ' +
                                values.city +
                                ' ' +
                                values.state
                              }
                            />
                            <DifferenceHighlighter string1={element?.suggestionZip} string2={values.postalCode} />
                          </section>
                        </section>
                      ))}
                    </RadioGroup>
                  </section>
                ) : (
                  <></>
                )}

                <section className="flex items-center space-x-2">
                  <RadioGroupItem value="edit" id="r2" onClick={() => handleRadioChange('edit')} />
                  <Label htmlFor="r2">
                    <p className="text-base font-semibold">{Texts.editYourAddress}</p>
                    {selectAddress === 'edit' ? <p className="text-xs font-normal">{Texts.requiredFields}</p> : <></>}
                  </Label>
                </section>
                {selectAddress === 'edit' ? (
                  <>
                    <section className="mt-2 flex max-w-[356px] items-center gap-2 border-l-4 border-l-semanticGold bg-almond py-3 text-sm text-semanticGold lg:ml-6">
                      <Icon className="ml-3 h-5 w-5" />
                      {Texts.verifyEditAddressAlert}
                    </section>
                    <FormFields
                      Texts={Texts}
                      FORM_FIELD_SPECS={FORM_FIELD_SPECS}
                      register={register}
                      errors={errors}
                      showadditionalAddressInfo={showadditionalAddressInfo}
                      handleShowAdditionalAddressInfo={handleShowAdditionalAddressInfo}
                      values={values}
                      setValue={setValue}
                    />
                  </>
                ) : (
                  <></>
                )}

                <section className="flex items-center space-x-2">
                  <RadioGroupItem value="original" id="r3" onClick={() => handleRadioChange('original')} />
                  <Label htmlFor="r3" className="text-base font-semibold">
                    {Texts.originalAddress}
                  </Label>
                </section>
                {selectAddress === 'original' ? (
                  <section className="ml-6">
                    <section className="mt-2 flex max-w-[356px] items-center gap-2 border-l-4 border-l-semanticGold bg-almond py-3 text-sm text-black">
                      <Icon className="ml-3 h-5 w-5" />
                      {Texts.originalAddressAlert}
                    </section>
                    <section className="pt-5">
                      <p>{values?.streetName}</p>
                      <p> {values?.additionalAddressInfo}</p>
                      <p>{values?.city}</p>
                      <p>
                        {' '}
                        {values?.state} {values?.postalCode}
                      </p>
                    </section>
                  </section>
                ) : (
                  <></>
                )}
              </RadioGroup>
            </section>
          ) : (
            <></>
          )}
          <section className="mt-8 flex justify-center">
            <Button
              type="submit"
              className="w-[337px] bg-deepBlue py-4 text-lg font-bold leading-7 text-white hover:bg-deepBlue lg:py-3 lg:text-lg lg:leading-6"
            >
              {Texts.continue}
            </Button>
          </section>
        </form>
      </DialogContent>
    </Dialog>
  );
}
